<template>
  <div class="self-page">
    <div class="back-row">
      <button type="button" class="back" @click="$emit('back')">
        <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true">
          <path d="M19 12H6M12 6l-6 6 6 6" fill="none" stroke="currentColor"
                stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
        返回
      </button>
      <span class="crumb">{{ subjectName || '未命名' }} · {{ scopeName }} · {{ roleName }}</span>
    </div>

    <!-- 顶部说明（居中） -->
    <header class="page-header">
      <h1>数据贯通成熟度等级</h1>
      <p>
        左侧为自评定位图，右侧为等级对照表。请结合两者的示意，判断贵单位（项目）当前
        所处的位置。<strong>本步为自评预判，不参与后续打分</strong>，仅在报告中与最终评价等级并列对照。
      </p>
    </header>

    <!-- 主体：左侧定位图 + 右侧等级对照表 -->
    <div class="content-row">
      <section class="chart-card">
        <MaturityChart :model-value="normalized" @select="pick" />
        <p class="chart-tip">点击气泡即可选择</p>
      </section>

      <section class="table-card">
        <el-table
          :data="rows"
          :span-method="spanMethod"
          :header-cell-style="headerCellStyle"
          :row-class-name="rowClassName"
          border
          class="level-table"
          @row-click="onRowClick"
        >
          <el-table-column prop="label" label="级别" width="78" align="center" />
          <el-table-column prop="desc" label="描述" min-width="260" />
          <el-table-column prop="mark" label="标志" width="150" align="center" />
          <el-table-column prop="env" label="环境" width="84" align="center" />
          <el-table-column prop="stage" label="阶段" width="90" align="center" />
          <el-table-column prop="mode" label="模式" width="112" align="center" />
        </el-table>
        <p class="chart-tip">点击表格行即可选择</p>
      </section>
    </div>

    <!-- 底部输入（居中） -->
    <footer class="page-footer">
      <p class="ask">根据上图与上表示意，对贵单位现状进行定性评价</p>

      <div class="answer-row">
        <el-input
          :model-value="selfCode"
          class="answer-input"
          size="large"
          placeholder="请输入评价，例如：L2a"
          clearable
          @update:model-value="onInput"
        />
        <span v-if="normalized" class="mapped">
          已选 <b>{{ labelOf(normalized) }}</b>
          <template v-if="mappedLevel">
            → 归并等级 <b>{{ mappedLevel }}</b>
          </template>
        </span>
        <span v-else-if="selfCode" class="mapped is-invalid">
          无法识别「{{ selfCode }}」，请参考 L0 / L1 / L2a / L2b / L3a / L3b / L4 / L5
        </span>
        <span v-else class="mapped is-empty">未选择（可跳过）</span>
      </div>

      <div class="actions">
        <el-button round @click="$emit('back')">上一步</el-button>
        <el-button type="primary" round @click="$emit('next')">
          下一步：开始评价
        </el-button>
      </div>
    </footer>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import MaturityChart from './MaturityChart.vue'
import {
  SELF_LEVELS, SELF_LEVEL_LABEL, SPAN_MATRIX, SCOPES, ROLE_MAP
} from '../data/index.js'
import { normalizeSelfCode, mapSelfLevel } from '../utils/scoring.js'

const props = defineProps({
  selfCode: { type: String, default: '' },
  subjectName: { type: String, default: '' },
  scope: { type: String, default: 'project' },
  roleId: { type: String, default: '' }
})

const emit = defineEmits(['back', 'next', 'update:selfCode'])

const rows = SELF_LEVELS
const headerCellStyle = {
  background: '#f5f5f7',
  color: '#1d1d1f',
  fontWeight: 600
}

const normalized = computed(() => normalizeSelfCode(props.selfCode))
const mappedLevel = computed(() => mapSelfLevel(props.selfCode))

const scopeName = computed(() => (SCOPES[props.scope] || {}).name || '')
const roleName = computed(() => (ROLE_MAP[props.roleId] || {}).name || '')

function labelOf (code) {
  return SELF_LEVEL_LABEL[code] || code
}

/** 点击定位图气泡或表格行：已选中则再次点击取消 */
function pick (code) {
  emit('update:selfCode', normalized.value === code ? '' : code)
}

function onRowClick (row) {
  pick(row.code)
}

function onInput (val) {
  emit('update:selfCode', val || '')
}

function rowClassName ({ row }) {
  return row.code === normalized.value ? 'row-selected' : ''
}

function spanMethod ({ rowIndex, columnIndex }) {
  return SPAN_MATRIX[rowIndex]?.[columnIndex] || [1, 1]
}
</script>

<style lang="scss" scoped>
.self-page {
  max-width: 1440px;
  margin: 0 auto;
  padding: 24px 24px 56px;
  color: #1d1d1f;
  font-family: -apple-system, BlinkMacSystemFont, 'PingFang SC', 'Helvetica Neue',
    'Microsoft YaHei', sans-serif;
}

.back-row {
  display: flex;
  align-items: center;
  gap: 14px;
}

.back {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 16px 7px 12px;
  font-size: 13px;
  color: #06c;
  background: rgba(0, 113, 227, 0.08);
  border: 0;
  border-radius: 999px;
  cursor: pointer;

  &:hover {
    background: rgba(0, 113, 227, 0.16);
  }
}

.crumb {
  font-size: 13px;
  color: #86868b;
}

.page-header {
  text-align: center;
  margin: 22px 0 26px;

  h1 {
    margin: 0;
    font-size: clamp(24px, 3.4vw, 34px);
    font-weight: 700;
    letter-spacing: 0.04em;
  }

  p {
    margin: 12px auto 0;
    max-width: 820px;
    font-size: 13.5px;
    line-height: 1.9;
    color: #6e6e73;

    strong {
      color: #d1681f;
      font-weight: 600;
    }
  }
}

.content-row {
  display: flex;
  gap: 24px;
  align-items: stretch;

  .chart-card,
  .table-card {
    background: #ffffff;
    border-radius: 18px;
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.06);
  }

  .chart-card {
    flex: 0 0 38%;
    padding: 16px;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  .table-card {
    flex: 1;
    min-width: 0;
    padding: 16px;
    overflow-x: auto;
  }
}

.chart-tip {
  margin: 10px 0 0;
  text-align: center;
  font-size: 12px;
  color: #a1a1a6;
}

.level-table {
  font-size: 13px;
  cursor: pointer;

  :deep(.row-selected) {
    background: rgba(0, 113, 227, 0.10);

    td {
      background: rgba(0, 113, 227, 0.10) !important;
    }
  }
}

.page-footer {
  text-align: center;
  margin-top: 32px;

  .ask {
    margin: 0 0 12px;
    font-size: 15px;
    color: #6e6e73;
  }
}

.answer-row {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

.answer-input {
  width: 360px;

  :deep(.el-input__wrapper) {
    border-radius: 999px;
  }
}

.mapped {
  font-size: 13px;
  color: #6e6e73;

  b {
    color: #0071e3;
  }

  &.is-invalid {
    color: #d1681f;
  }

  &.is-empty {
    color: #a1a1a6;
  }
}

.actions {
  display: flex;
  justify-content: center;
  gap: 14px;
  margin-top: 26px;
}

@media (max-width: 1100px) {
  .content-row {
    flex-direction: column;
  }

  .chart-card {
    flex: 1 1 auto;
  }
}
</style>
